import { createRouter, createWebHistory, RouterView } from 'vue-router';
import { CLIENT_LAYOUT_PATH, DOCS_PATH, FORGOT_PASSWORD_PATH, isAuthenticated, LOGIN_PATH, PORTAL_PATH, REGISTER_PATH, SIGNIN_PATH } from '@/utils/auth';

const routes = [
  {
    path: PORTAL_PATH,
    name: 'PortalHome',
    component: () => import('@/views/portal/portalHome.vue'),
    meta: {
      requiresAuth: false,
      seo: {
        lang: 'zh-CN',
        title: 'PolyDub - AI智能视频翻译配音扩展',
        desc: 'PolyDub - AI智能视频翻译配音扩展，支持 YouTube、Udemy、BiliBili、Coursera 等网站的实时字幕翻译与 AI 配音。核心功能：实时字幕翻译、AI 智能配音、多平台支持、本地视频字幕翻译。技术特色：基于 AI 深度学习的翻译引擎，支持 SRT/VTT，多语言与播放速度可调，个性化设置保存。适用场景：外语学习、观看国外影视、在线教育与培训、跨语言交流。',
        keywords: 'PolyDub, AI配音, 视频翻译, 字幕翻译, 实时字幕, YouTube 翻译, Udemy 翻译, BiliBili 翻译, Coursera 翻译, Azure TTS, Google TTS, SRT, VTT',
      },
    },
  },
  {
    path: DOCS_PATH,
    name: 'Docs',
    component: () => import('@/views/portal/doc.vue'),
    meta: { requiresAuth: false, seo: { lang: 'zh-CN', title: 'PolyDub 文档', desc: 'PolyDub 使用文档与指南' } },
  },
  {
    path: '/studio-guide',
    redirect: { path: DOCS_PATH, hash: '#studioConfig' },
  },
  {
    path: '/cookie-guide',
    redirect: { path: DOCS_PATH, hash: '#cookie' },
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: () => import('@/views/portal/privacyPolicy.vue'),
    meta: { requiresAuth: false, seo: { lang: 'zh-CN', title: '隐私政策 - PolyDub', desc: 'PolyDub 隐私政策说明' } },
  },
  {
    path: '/localPlayer',
    name: 'LocalPlayer',
    component: () => import('@/views/client/localPlayer.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/model-download',
    name: 'ModelDownload',
    component: () => import('@/views/client/model-download.vue'),
    meta: { requiresAuth: false },
  },

  {
    path: '/',
    name: 'Client',
    component: RouterView,
    meta: { requiresAuth: false },
    redirect: PORTAL_PATH,
    children: [
      {
        path: '/en',
        name: 'PortalHomeEN',
        component: () => import('@/views/portal/portalHome.vue'),
        meta: {
          requiresAuth: false,
          seo: {
            lang: 'en',
            title: 'PolyDub - AI Video Translation & Dubbing Extension',
            desc: 'PolyDub enables real-time subtitle translation and AI dubbing for YouTube, Udemy, BiliBili and Coursera. Key features: multi-language subtitle translation (EN/JA/KR/FR/DE and more), AI dubbing powered by Microsoft Azure and Google TTS, support for SRT/VTT, local video subtitle translation, adjustable speed/volume, and personalized settings. Use cases: language learning, watching foreign content, online education and cross-language communication.',
            keywords: 'PolyDub, AI dubbing, video translation, subtitle translation, real-time subtitles, YouTube translator, Udemy translator, BiliBili translator, Coursera translator, Azure TTS, Google TTS, SRT, VTT',
          },
        },
      },
      {
        path: SIGNIN_PATH,
        name: 'SignIn',
        component: () => import('@/views/client/signin.vue'),
        meta: { requiresAuth: false },
      },
      {
        path: REGISTER_PATH,
        name: 'Register',
        component: () => import('@/views/client/register.vue'),
        meta: { requiresAuth: false },
      },
      {
        path: FORGOT_PASSWORD_PATH,
        name: 'ForgotPassword',
        component: () => import('@/views/client/forgot-password.vue'),
        meta: { requiresAuth: false },
      },

      {
        path: CLIENT_LAYOUT_PATH,
        name: 'ClientLayout',
        component: () => import('@/views/client/layout.vue'),
        redirect: CLIENT_LAYOUT_PATH + '/subscription',
        meta: { requiresAuth: true },
        children: [
          {
            path: 'subscription',
            name: 'ClientSubscription',
            component: () => import('@/views/client/subscription.vue'),
            meta: { requiresAuth: true },
          },
          {
            path: 'billing',
            name: 'ClientBilling',
            component: () => import('@/views/client/billing.vue'),
            meta: { requiresAuth: true },
          },
        ],
      },
    ],
  },
  {
    path: LOGIN_PATH,
    name: 'Login',
    component: () => import('@/views/admin/login.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/adminLayout',
    name: 'AdminLayout',
    component: () => import('@/views/admin/layout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/admin/dashboard.vue'),
        meta: { requiresAuth: true, menuCode: 'dashboard' },
      },
      {
        path: 'user',
        name: 'User',
        component: () => import('@/views/admin/user.vue'),
        meta: { requiresAuth: true, menuCode: 'user' },
      },
      {
        path: 'user-chars-period',
        name: 'UserCharsPeriod',
        component: () => import('@/views/admin/user-chars-period.vue'),
        meta: { requiresAuth: true, menuCode: 'user-chars-period' },
      },
      {
        path: 'play-records',
        name: 'PlayRecords',
        component: () => import('@/views/admin/play-records.vue'),
        meta: { requiresAuth: true, menuCode: 'play-records' },
      },
      {
        path: 'system/config',
        name: 'SystemConfig',
        component: () => import('@/views/admin/system-config.vue'),
        meta: { requiresAuth: true, menuCode: 'system/config' },
      },
      {
        path: 'order-records',
        name: 'OrderRecords',
        component: () => import('@/views/admin/order-records.vue'),
        meta: { requiresAuth: true, menuCode: 'order-records' },
      },
      {
        path: 'realtime-logs',
        name: 'RealtimeLogs',
        component: () => import('@/views/admin/realtime-logs.vue'),
        meta: { requiresAuth: true, menuCode: 'realtime-logs' },
      },
    ],
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'NotFoundWildcard',
    redirect: to => {
      if (to.path === '/') {
        return PORTAL_PATH;
      }
      return '/404';
    },
  },
  {
    path: '/403',
    name: 'Forbidden',
    component: () => import('@/views/403/index.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/404',
    name: 'NotFound',
    component: () => import('@/views/404/index.vue'),
    meta: { requiresAuth: false },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// 动态注入 SEO meta
function setMetaTag(name, content) {
  if (!content) {
    return;
  }
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

router.afterEach(to => {
  const seo = to.meta?.seo;
  if (seo) {
    document.title = seo.title;
    setMetaTag('description', seo.desc);
    setMetaTag('keywords', seo.keywords);
    // 更新 OpenGraph/Twitter
    const setOG = (p, content) => {
      if (!content) {
        return;
      }
      let el = document.querySelector(`meta[property="${p}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', p);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    const pageUrl = `${window.location.origin}${to.path}`;
    setOG('og:title', seo.title);
    setOG('og:description', seo.desc);
    setOG('og:url', pageUrl);
    // canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', pageUrl);

    document.documentElement.lang = seo.lang || 'zh-CN';
  }
});

router.beforeEach(async to => {
  // 公开路由直接通过
  if ([SIGNIN_PATH, REGISTER_PATH, PORTAL_PATH, DOCS_PATH, LOGIN_PATH, FORGOT_PASSWORD_PATH, '/privacy-policy', '/studio-guide', '/cookie-guide', '/en', '/sitemap.xml', '/robots.txt', '/favicon.ico'].includes(to.path)) {
    return true;
  }
  // 放行静态资源与 index.html 直链访问
  if (/\.(xml|txt|ico|png|jpg|jpeg|webp|svg|gif|html)$/i.test(to.path)) {
    return true;
  }
  if (!isAuthenticated() && to.meta.requiresAuth) {
    return SIGNIN_PATH;
  }
  return true;
});

export default router;
